import { SettingsProps } from 'client/components/core/Sidebar/components/Settings/entities'
import {
  useUpdateEntity,
  useUpdatePropNew,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { getSettingsEditing } from 'client/reducers/managementReducer'
import { useManagement, usePage } from 'client/store'
import { setSettingsEditing } from 'client/store/management/managementActions'
import { isWebsitePageType } from 'client/store/page/pageSelectors'
import { AlignSelfType } from 'common/types/styleTypes'
import React, { useCallback, useMemo } from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { SettingsNestedMenu } from 'src/editor/layout/sidebar/settings/settings-nested-menu'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { ResetEntityStyles } from 'src/editor/shared/settings/reset-entity-styles-button'
import { EntityBorderGroup } from 'src/editor/shared/settings/wrappers/border-groups/entity-border-group'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import { SelectNew } from 'src/editor/shared/ui-lib/select-new/select-new'
import { Switcher } from 'src/editor/shared/ui-lib/switcher'
import {
  FormLayoutEnum,
  FormSubEntityTypeEnum,
} from 'src/shared/entities/form/enums'
import { FormInterface } from 'src/shared/entities/form/form.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { WebsiteSettingsTypographyTypeEnum } from 'src/shared/types/website-settings-types'
import { getMobileOrDesktop } from 'src/shared/utils/get-mobile-or-desktop'
import { FormButtonCommonSettings } from './form-button-common-settings'
import { FormCheckboxesCommonSettings } from './form-checkboxes-common-settings'
import { FormFieldsCommonSettings } from './form-fields-common-settings'
import { FormLayoutSwitcher } from './form-layout-switcher'

interface FormDesignSettingsProps extends SettingsProps<FormInterface> {}

export const FormDesignSettings = ({
  entity,
  defaultEntity,
}: FormDesignSettingsProps) => {
  const { isMobile } = useDeviceModeContext()
  const { t } = useTypedTranslation()
  const dispatch = useAppDispatch()

  const isWebsitePage = usePage(isWebsitePageType)
  const editingFormCommonSettingsType =
    useManagement(getSettingsEditing).commonSettingsType

  const updateEntity = useUpdateEntity<FormInterface>()
  const updateProp = useUpdatePropNew(entity)

  const buttonPositionOptions = useMemo(
    () => [
      { value: 'flex-start' as AlignSelfType, label: t('left') },
      { value: 'center' as AlignSelfType, label: t('center') },
      { value: 'flex-end' as AlignSelfType, label: t('right') },
      { value: 'full' as const, label: t('full') },
    ],
    [t],
  )

  const removeCommonSettingsFromEditing = useCallback(() => {
    dispatch(
      setSettingsEditing({
        id: entity.id,
        type: entity.type,
        commonSettingsType: undefined,
      }),
    )
  }, [entity.id, entity.type, dispatch])

  return (
    <>
      <GroupTitle
        withoutHorizontalLine
        afterChildren={
          <ResetEntityStyles
            entity={entity}
            defaultEntity={defaultEntity}
            keys={['layout', 'rowGap', 'columnGap', 'backgroundColor']}
            mobileKeys={['mobileRowGap', 'mobileBackgroundColor']}
          />
        }
      >
        entity_settings.form.layout
      </GroupTitle>
      {!isMobile && (
        <FormLayoutSwitcher
          className="mt-4"
          value={entity.layout}
          update={updateProp('layout')}
        />
      )}
      <RangeElement
        label="entity_settings.form.design.row_gap"
        value={getMobileOrDesktop(entity.rowGap, entity.mobileRowGap, isMobile)}
        onChange={updateProp(
          getMobileOrDesktop('rowGap', 'mobileRowGap', isMobile),
        )}
      />
      {!isMobile && entity.layout === FormLayoutEnum.TwoColumns && (
        <RangeElement
          label="entity_settings.form.design.column_gap"
          value={entity.columnGap}
          onChange={updateProp('columnGap')}
        />
      )}
      <ColorPickerControl
        label="settings_styles.background_color.label"
        color={entity.backgroundColor}
        update={updateProp('backgroundColor')}
        mobileColor={entity.mobileBackgroundColor}
        mobileUpdate={updateProp('mobileBackgroundColor')}
      />

      <EntityBorderGroup
        entity={entity}
        defaultEntity={defaultEntity}
        propertyKey="border"
        mobilePropertyKey="mobileBorder"
      />

      <SizeAndPositionGroup
        padding={isMobile ? entity.mobilePadding : entity.padding}
        defaultPadding={
          isMobile ? defaultEntity?.mobilePadding : defaultEntity?.padding
        }
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />

      <GroupTitle>entity_settings.form.design.common_fields.title</GroupTitle>
      <SettingsNestedMenu
        menuTitle={t('entity_settings.form.design.common_fields.title')}
        menuButtonLabel={t('settings.sidebar.customize')}
        menuTitleClassName="mb-6"
        isOpen={
          typeof editingFormCommonSettingsType === 'undefined'
            ? undefined
            : editingFormCommonSettingsType === FormSubEntityTypeEnum.Field
        }
        onClose={removeCommonSettingsFromEditing}
      >
        <FormFieldsCommonSettings
          entity={entity}
          defaultEntity={defaultEntity}
        />
      </SettingsNestedMenu>

      <GroupTitle>entity_settings.form.design.common_button.title</GroupTitle>
      {isWebsitePage && (
        <Switcher
          labelText="settings_styles.type.label"
          btnList={[
            {
              value: WebsiteSettingsTypographyTypeEnum.PrimaryButton,
              content:
                'website_styles_settings.buttons_and_links.primary.title',
            },
            {
              value: WebsiteSettingsTypographyTypeEnum.SecondaryButton,
              content:
                'website_styles_settings.buttons_and_links.secondary.title',
            },
          ]}
          value={entity.buttonCommonStyles.buttonType}
          update={updateProp('buttonCommonStyles', 'buttonType')}
        />
      )}
      <SelectNew
        isSearchable={false}
        labelText="entity_settings.form.design.common_button.position"
        selectedOption={
          buttonPositionOptions.find(option =>
            entity.buttonCommonStyles.width === '100%'
              ? option.value === 'full'
              : option.value === entity.buttonCommonStyles.alignSelf,
          ) ?? null
        }
        options={buttonPositionOptions}
        onChange={async option => {
          if (option) {
            if (option.value === 'full') {
              updateProp('buttonCommonStyles', 'width')('100%')
            } else {
              updateEntity({
                ...entity,
                buttonCommonStyles: {
                  ...entity.buttonCommonStyles,
                  width: 'auto',
                  alignSelf: option.value,
                },
              })
            }
          }
        }}
      />
      <GroupTitle className="mb-0" />
      <SettingsNestedMenu
        menuTitle={t('entity_settings.form.design.common_button.title')}
        menuButtonLabel={t('settings.sidebar.customize')}
        menuTitleClassName="mb-6"
        isOpen={
          typeof editingFormCommonSettingsType === 'undefined'
            ? undefined
            : editingFormCommonSettingsType ===
              FormSubEntityTypeEnum.SubmitButton
        }
        onClose={removeCommonSettingsFromEditing}
      >
        <FormButtonCommonSettings
          entity={entity}
          defaultEntity={defaultEntity}
        />
      </SettingsNestedMenu>

      <GroupTitle>entity_settings.form.design.common_checkbox.title</GroupTitle>
      <SettingsNestedMenu
        menuTitle={t('entity_settings.form.design.common_checkbox.title')}
        menuButtonLabel={t('settings.sidebar.customize')}
        menuTitleClassName="mb-6"
        isOpen={
          typeof editingFormCommonSettingsType === 'undefined'
            ? undefined
            : editingFormCommonSettingsType === FormSubEntityTypeEnum.Checkbox
        }
        onClose={removeCommonSettingsFromEditing}
      >
        <FormCheckboxesCommonSettings
          entity={entity}
          defaultEntity={defaultEntity}
        />
      </SettingsNestedMenu>

      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}
